// Import uniwersalnego wrappera fetchującego (GET/POST/DELETE itd.)
import { fetchWrapper } from "@/helpers/fetch-wrapper";

// Klasa obsługująca operacje na kontaktach (np. email, telefon)
class ContactsListService {

  /**
   * Dodaje nowy kontakt do systemu.
   * @param {string} contact - Adres e-mail lub numer telefonu.
   * @param {number} typeOfContact - Typ kontaktu (np. 0 = email, 1 = telefon).
   * @param {string} description - Opis kontaktu (opcjonalny — może być pusty).
   * @returns odpowiedź z API lub rzuca błąd, jeśli brakuje wymaganych pól.
   */
  async postNewContact(contact, typeOfContact, description = '', contactGroupIds = []) {
    // Walidacja – kontakt i typ wymagane, opis opcjonalny
    if (!contact || typeOfContact === undefined) {
      throw new Error('Contact and typeOfContact are required.');
    }

    // Jeśli typ kontaktu jest przekazany jako string, konwertuj na liczbę
    if (typeof(typeOfContact) == 'string') {
      typeOfContact = Number(typeOfContact);
    }

    // Wysłanie POST na endpoint /wanted-contacts
    const response = await fetchWrapper.post(
      process.env.VUE_APP_API_URL + '/wanted-contacts',
      {
        contact: contact,
        typeOfContact: typeOfContact,
        description: description ?? '',
        contactGroupIds: Array.isArray(contactGroupIds) ? contactGroupIds : [],
      }
    );
    return response;
  }

  /**
   * Dodaje istniejącego użytkownika systemu jako kontakt. Wartość kontaktu (e-mail/telefon)
   * wylicza serwer z danych konta na podstawie wybranego typu; kontakt zostaje automatycznie
   * powiązany z userem, dzięki czemu user zyskuje widoczność grup ORAZ powiadomienia.
   *
   * @param {number} userId - ID użytkownika systemu.
   * @param {number} typeOfContact - Typ kontaktu (0 = e-mail, 1 = SMS, 2 = MMS).
   * @param {string} description - Opis kontaktu.
   */
  async postNewContactForUser(userId, typeOfContact, description = '', contactGroupIds = []) {
    if (userId === undefined || userId === null || typeOfContact === undefined) {
      throw new Error('userId and typeOfContact are required.');
    }

    return fetchWrapper.post(
      process.env.VUE_APP_API_URL + '/wanted-contacts',
      {
        userId: Number(userId),
        typeOfContact: Number(typeOfContact),
        description: description ?? '',
        contactGroupIds: Array.isArray(contactGroupIds) ? contactGroupIds : [],
      }
    );
  }

  /**
   * Aktualizuje wartość (e-mail/telefon) i opis kontaktu. Typu kontaktu ani przypisania
   * do grup ten endpoint nie zmienia — grupy edytuje się w formularzu grupy.
   * @param {number} id - ID kontaktu.
   * @param {string} contact - Nowa wartość kontaktu.
   * @param {string} description - Nowy opis kontaktu.
   */
  async update(id, contact, description) {
    return fetchWrapper.put(
      `${process.env.VUE_APP_API_URL}/wanted-contacts/${id}`,
      {
        contact: contact,
        description: description,
      }
    );
  }

  /**
   * Pobiera wszystkie kontakty (sortowanie malejąco). Limit jak przy listach tablic i grup —
   * zakładka „Kontakty” ma pokazywać wszystkie wpisy, do których użytkownik ma wgląd.
   */
  async getAll() {
    try {
      // Parametry zapytania GET (paginacja)
      const params = new URLSearchParams({
        orderDirection: 'desc', // sortowanie malejąco
        page: 1,
        limit: 1000
      });

      // Pobranie kontaktów
      const response = await fetchWrapper.get(
        `${process.env.VUE_APP_API_URL}/wanted-contacts?${params.toString()}`
      );

      return response;
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  }

  /**
   * Usuwa kontakt po ID.
   * @param {number} id - ID kontaktu do usunięcia.
   */
  async delete(id) {
    try {
      const response = await fetchWrapper.delete(
        `${process.env.VUE_APP_API_URL}/wanted-contacts/${id}`
      );
      return response;
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  }
}

// Eksport pojedynczej instancji serwisu
export default new ContactsListService();
